import type { ProjectDocumentV1 } from '@dg-embalajes/contracts';
import { getV46FontProfile } from '@dg-embalajes/font-library';
import {
  PDF_SCENE_SCHEMA_VERSION,
  assertPdfScene,
  type PdfSceneV1,
  type SceneNode,
} from '@dg-embalajes/pdf-engine';

export function createPrintablePdfScene(document: ProjectDocumentV1): PdfSceneV1 {
  const product: PdfSceneV1['product'] = document.product.kind === 'tube'
    ? { ...document.product }
    : {
        kind: 'sticker',
        shape: document.product.shape,
        sourceLabel: document.product.source.kind === 'preset'
          ? document.product.source.presetCode
          : 'personalizado',
        widthMm: document.product.widthMm,
        heightMm: document.product.heightMm,
        cutLineRule: 'v46-300dpi',
      };

  const background: PdfSceneV1['background'] = document.background.kind === 'white'
    ? { kind: 'white' }
    : document.background.kind === 'catalog'
      ? { kind: 'asset', assetId: `catalog:${document.background.patternId}`, fit: 'cover' }
      : {
          kind: 'asset',
          assetId: document.background.assetId,
          fit: 'cover',
          transform: document.background.transform,
          ...(document.background.pdfPageIndex === null
            ? {}
            : { pdfPageIndex: document.background.pdfPageIndex }),
        };

  const nodes: SceneNode[] = document.nodes
    .filter((node) => node.visible)
    .map((node) => {
      if (node.type === 'image') {
        return {
          id: node.id,
          type: 'asset',
          assetId: node.assetId,
          transform: node.transform,
          ...(node.pdfPageIndex === null ? {} : { pdfPageIndex: node.pdfPageIndex }),
        };
      }
      const font = getV46FontProfile(node.fontFamily);
      return {
        id: node.id,
        type: 'text',
        transform: node.transform,
        text: node.text,
        font: {
          kind: 'embedded',
          fontAssetId: font.fontAssetId,
          family: font.family,
          weight: font.weight,
          style: font.style,
        },
        fontSizeMm: node.fontSizeMm,
        fillColor: node.fillColor,
        stroke: node.stroke,
      };
    });

  const scene: PdfSceneV1 = {
    schemaVersion: PDF_SCENE_SCHEMA_VERSION,
    documentId: document.projectId,
    product,
    widthMm: document.product.kind === 'tube'
      ? document.product.developmentEffectiveMm
      : document.product.widthMm,
    heightMm: document.product.kind === 'tube'
      ? document.product.heightMm
      : document.product.heightMm,
    background,
    nodes,
  };
  assertPdfScene(scene);
  return scene;
}
